import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  MapPin,
  Plus,
  Trash2,
  Edit,
  Car,
  Bike,
  Zap,
  Accessibility,
  AlertCircle,
  Forklift,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { number } from "zod";

const AdminSlotsPage = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [newSlot, setNewSlot] = useState({
    slot_number: "",
    floor: "",
    location: "",
    slot_type: "",
    number_of_vehicles_parked: 0,
    hydrolic_lift_slot: false
  });
  const navigate = useNavigate();

  // Color coding and icons for slot types
  const slotTypeConfig = {
    car: {
      color: "bg-gray-100 border-gray-400",
      textColor: "text-gray-900",
      iconColor: "text-gray-900",
      icon: Car,
    },
    bike: {
      color: "bg-yellow-100 border-yellow-400",
      textColor: "text-yellow-900",
      iconColor: "text-yellow-900",
      icon: Bike,
    },
    electric_car: {
      color: "bg-green-100 border-green-400",
      textColor: "text-green-900",
      iconColor: "text-green-900",
      icon: Zap,
    },
    electric_bike: {
      color: "bg-green-100 border-green-400",
      textColor: "text-green-900",
      iconColor: "text-green-900",
      icon: Zap,
    },
    handicapped: {
      color: "bg-blue-100 border-blue-400",
      textColor: "text-blue-900",
      iconColor: "text-blue-900",
      icon: Accessibility,
    },
    hydrolic_lift_slot:{
      color: "bg-purple-100 border-purple-400",
      textColor: "text-purple-900",
      iconColor: "text-purple-900",
      icon: Forklift,
    }
  };

  const getSlotTypeConfig = (type) => {
    return (
      slotTypeConfig[type] || {
        color: "bg-red-100 border-red-400",
        textColor: "text-red-900",
        iconColor: "text-red-900",
        icon: AlertCircle,
      }
    );
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${API}/parking/slots`);
      setSlots(response.data);
    } catch (error) {
      toast.error("Failed to load parking slots");
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();

    try {
      const data = {
        ...newSlot,
        number_of_vehicles_parked: newSlot?.slot_type !== "car" && newSlot?.slot_type !=='electric_car' ? newSlot?.number_of_vehicles_parked : 1
      }
      await axios.post(`${API}/admin/slots`, data);
      toast.success("Parking slot added successfully!");
      setShowAddDialog(false);
      setNewSlot({ slot_number: "", floor: "", location: "" });
      fetchSlots();
    } catch (error) {
      toast.error("Failed to add slot");
    }
  };

  const handleEditSlot = (slot) => {
    setEditingSlot({
      _id: slot._id,
      slot_number: slot.slot_number,
      floor: slot.floor,
      location: slot.location,
      slot_type: slot.slot_type,
      hydrolic_lift_slot: slot.hydrolic_lift_slot,
      number_of_vehicles_parked: slot.number_of_vehicles_parked
    });
    setShowEditDialog(true);
  };

  const handleUpdateSlot = async (e) => {
    e.preventDefault();

    try {
      await axios.put(`${API}/admin/slots/${editingSlot._id}`, {
        slot_number: editingSlot.slot_number,
        floor: editingSlot.floor,
        location: editingSlot.location,
        slot_type: editingSlot.slot_type,
        hydrolic_lift_slot: editingSlot.hydrolic_lift_slot,
        number_of_vehicles_parked: 
        editingSlot?.slot_type !== "car" && editingSlot?.slot_type !=='electric_car' ? editingSlot?.number_of_vehicles_parked : 1
      });
      toast.success("Parking slot updated successfully!");
      setShowEditDialog(false);
      setEditingSlot(null);
      fetchSlots();
    } catch (error) {
      toast.error("Failed to update slot");
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm("Are you sure you want to delete this slot?")) return;

    try {
      await axios.delete(`${API}/admin/slots/${slotId}`);
      toast.success("Slot deleted");
      fetchSlots();
    } catch (error) {
      toast.error("Failed to delete slot");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-slots-page">
      <div className="pt-12 pb-12 px-6 max-w-7xl mx-auto">
        <Button
          data-testid="back-to-admin"
          onClick={() => navigate("/admin")}
          variant="ghost"
          className="mb-6 text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Admin
        </Button>

        <div className="fade-in">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">
                Parking Slot Management
              </h1>
              <p className="text-xl text-gray-600">
                Configure parking capacity and slots.
              </p>
            </div>

            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-slot-btn"
                  className="bg-[#FF6B35] hover:bg-[#E55A28] text-white rounded-full px-6 py-6"
                >
                  <Plus className="mr-2" size={20} />
                  Add Slot
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold text-[#1A3C34]">
                    Add New Parking Slot
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddSlot} className="space-y-4 mt-4">
                  <div>
                    <Label
                      htmlFor="slot_number"
                      className="text-gray-700 font-medium"
                    >
                      Slot Number
                    </Label>
                    <Input
                      id="slot_number"
                      data-testid="slot-number"
                      placeholder="A-101"
                      value={newSlot.slot_number}
                      onChange={(e) =>
                        setNewSlot({ ...newSlot, slot_number: e.target.value })
                      }
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="floor"
                      className="text-gray-700 font-medium"
                    >
                      Floor
                    </Label>
                    <Input
                      id="floor"
                      data-testid="slot-floor"
                      placeholder="Ground Floor"
                      value={newSlot.floor}
                      onChange={(e) =>
                        setNewSlot({ ...newSlot, floor: e.target.value })
                      }
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="location"
                      className="text-gray-700 font-medium"
                    >
                      Location
                    </Label>
                    <Input
                      id="location"
                      data-testid="slot-location"
                      placeholder="West Wing"
                      value={newSlot.location}
                      onChange={(e) =>
                        setNewSlot({ ...newSlot, location: e.target.value })
                      }
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    />
                  </div>
                  <div>
                  <Label
                    htmlFor="edit_slot_type"
                    className="text-gray-700 font-medium"
                  >
                    Hydrolic Lift Slot
                  </Label>
                  <Select
                    value={newSlot?.hydrolic_lift_slot || ""}
                    onValueChange={(value) =>
                      setNewSlot({
                        ...newSlot,
                        hydrolic_lift_slot: value,
                      })
                    }
                  >
                    <SelectTrigger className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12 text-base">
                      <SelectValue placeholder="Select Slot Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                  <div>
                    <Label
                      htmlFor="slot_type"
                      className="text-gray-700 font-medium"
                    >
                      Slot For
                    </Label>
                    <Select
                      id="slot_type"
                      data-testid="slot-type"
                      value={newSlot.slot_type}
                      onValueChange={(value) =>
                        setNewSlot({ ...newSlot, slot_type: value })
                      }
                      className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                      required
                    >
                      <SelectTrigger className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12 text-base">
                        <SelectValue placeholder="Select Slot Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="car">Car</SelectItem>
                        <SelectItem value="bike">Bike</SelectItem>
                        <SelectItem value="handicapped">Handicapped</SelectItem>
                        <SelectItem value="electric_car">
                          Electric Car
                        </SelectItem>
                        <SelectItem value="electric_bike">
                          Electric Bike
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                  <Label
                    htmlFor="edit_location"
                    className="text-gray-700 font-medium"
                  >
                    Number of vehicles parked
                  </Label>
                  <Input
                    id="edit_number_of_vehicles_parked"
                    data-testid="edit-slot-number-of-vehicles-parked"
                    placeholder="0"
                    type="number"
                    value={
                      newSlot?.slot_type !== "car" && newSlot?.slot_type !=='electric_car' ? newSlot?.number_of_vehicles_parked : 1
                    }
                    disabled={newSlot?.slot_type === "car" || newSlot?.slot_type === 'electric_car'}
                    onChange={(e) =>
                      setNewSlot({
                        ...newSlot,
                        number_of_vehicles_parked: newSlot?.slot_type !== "car" && newSlot?.slot_type !=='electric_car' ? parseInt(e.target.value) || 0 : 1,
                      })
                    }
                    className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                    required
                  />
                </div>
                  <Button
                    data-testid="submit-slot"
                    type="submit"
                    className="w-full bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full py-6"
                  >
                    Add Slot
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Edit Slot Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#1A3C34]">
                  Edit Parking Slot
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateSlot} className="space-y-4 mt-4">
                <div>
                  <Label
                    htmlFor="edit_slot_number"
                    className="text-gray-700 font-medium"
                  >
                    Slot Number
                  </Label>
                  <Input
                    id="edit_slot_number"
                    data-testid="edit-slot-number"
                    placeholder="A-101"
                    value={editingSlot?.slot_number || ""}
                    onChange={(e) =>
                      setEditingSlot({
                        ...editingSlot,
                        slot_number: e.target.value,
                      })
                    }
                    className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                    required
                  />
                </div>
                <div>
                  <Label
                    htmlFor="edit_floor"
                    className="text-gray-700 font-medium"
                  >
                    Floor
                  </Label>
                  <Input
                    id="edit_floor"
                    data-testid="edit-slot-floor"
                    placeholder="Ground Floor"
                    value={editingSlot?.floor || ""}
                    onChange={(e) =>
                      setEditingSlot({
                        ...editingSlot,
                        floor: e.target.value,
                      })
                    }
                    className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                    required
                  />
                </div>
                <div>
                  <Label
                    htmlFor="edit_location"
                    className="text-gray-700 font-medium"
                  >
                    Location
                  </Label>
                  <Input
                    id="edit_location"
                    data-testid="edit-slot-location"
                    placeholder="West Wing"
                    value={editingSlot?.location || ""}
                    onChange={(e) =>
                      setEditingSlot({
                        ...editingSlot,
                        location: e.target.value,
                      })
                    }
                    className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                    required
                  />
                </div>
                <div>
                  <Label
                    htmlFor="edit_slot_type"
                    className="text-gray-700 font-medium"
                  >
                    Hydrolic Lift Slot
                  </Label>
                  <Select
                    value={editingSlot?.hydrolic_lift_slot || ""}
                    onValueChange={(value) =>
                      setEditingSlot({
                        ...editingSlot,
                        hydrolic_lift_slot: value,
                      })
                    }
                  >
                    <SelectTrigger className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12 text-base">
                      <SelectValue placeholder="Select Slot Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label
                    htmlFor="edit_slot_for"
                    className="text-gray-700 font-medium"
                  >
                    Slot For
                  </Label>
                  <Select
                    value={editingSlot?.slot_type || ""}
                    onValueChange={(value) =>
                      setEditingSlot({
                        ...editingSlot,
                        slot_type: value,
                      })
                    }
                    defaultValue={editingSlot?.slot_type || ""}
                  >
                    <SelectTrigger className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12 text-base">
                      <SelectValue placeholder="Select Slot Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="car">Car</SelectItem>
                      <SelectItem value="bike">Bike</SelectItem>
                      <SelectItem value="handicapped">Handicapped</SelectItem>
                      <SelectItem value="electric_car">Electric Car</SelectItem>
                      <SelectItem value="electric_bike">Electric Bike</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                 <div>
                  <Label
                    htmlFor="edit_location"
                    className="text-gray-700 font-medium"
                  >
                    Number of vehicles parked
                  </Label>
                  <Input
                    id="edit_number_of_vehicles_parked"
                    data-testid="edit-slot-number-of-vehicles-parked"
                    placeholder="0"
                    type="number"
                    value={
                      editingSlot?.slot_type !== "car" && editingSlot?.slot_type !=='electric_car' ? editingSlot?.number_of_vehicles_parked : 1
                    }
                    disabled={editingSlot?.slot_type === "car" || editingSlot?.slot_type === 'electric_car'}
                    onChange={(e) =>
                      setEditingSlot({
                        ...editingSlot,
                        number_of_vehicles_parked: editingSlot?.slot_type !== "car" && editingSlot?.slot_type !=='electric_car' ? parseInt(e.target.value) || 0 : 1,
                      })
                    }
                    className="mt-2 bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-12"
                    required
                  />
                </div>
                <Button
                  data-testid="submit-edit-slot"
                  type="submit"
                  className="w-full bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full py-6"
                >
                  Update Slot
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6 bg-[#1A3C34] text-white border-none">
              <p className="text-sm opacity-90 mb-2">Total Slots</p>
              <p className="text-4xl font-bold">{slots.length}</p>
            </Card>
            <Card className="p-6 bg-[#F5F2EB] border-none">
              <p className="text-sm text-gray-600 mb-2">Available</p>
              <p className="text-4xl font-bold text-[#1A3C34]">
                {slots.filter((s) => !s.is_booked).length}
              </p>
            </Card>
            <Card className="p-6 bg-[#FF6B35] text-white border-none">
              <p className="text-sm opacity-90 mb-2">Booked</p>
              <p className="text-4xl font-bold">
                {slots.filter((s) => s.is_booked).length}
              </p>
            </Card>
          </div>

          {/* Slots Grid */}
          {slots.length === 0 ? (
            <Card className="p-12 text-center">
              <MapPin className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-600 text-lg mb-4">
                No parking slots configured
              </p>
              <Button
                data-testid="add-first-slot"
                onClick={() => setShowAddDialog(true)}
                className="bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full px-6"
              >
                <Plus className="mr-2" size={20} />
                Add Your First Slot
              </Button>
            </Card>
          ) : (
            <Card className="p-6 border border-gray-100">
              <h2 className="text-2xl font-bold text-[#1A3C34] mb-6">
                All Parking Slots
              </h2>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                {slots.map((slot) => (
                  <div
                    key={slot._id}
                    data-testid={`slot-${slot._id}`}
                    className={`p-6 rounded-xl border-2 transition-all relative ${
                      slot.is_booked
                        ? "border-red-300 bg-red-50"
                        : "border-green-300 bg-green-50"
                    }`}
                  >
                    <div className="absolute top-2 right-2 flex gap-2 opacity-0 hover:opacity-100 transition-opacity">
                      <Button
                        data-testid={`edit-slot-${slot._id}`}
                        onClick={() => handleEditSlot(slot)}
                        variant="ghost"
                        className="text-blue-600 hover:bg-blue-100 rounded-full p-2 h-8 w-8"
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        data-testid={`delete-slot-${slot._id}`}
                        onClick={() => handleDeleteSlot(slot._id)}
                        variant="ghost"
                        className="text-red-600 hover:bg-red-100 rounded-full p-2 h-8 w-8"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>

                    <div className="w-12 h-12 bg-[#1A3C34] rounded-xl flex items-center justify-center mb-4">
                      <MapPin className="text-white" size={24} />
                    </div>

                    <h3 className="text-2xl font-bold text-[#1A3C34] mb-2">
                      {slot.slot_number}
                    </h3>

                    <div className="space-y-1 mb-3">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Floor:</span> {slot.floor}
                      </p>
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Location:</span>{" "}
                        {slot.location}
                      </p>
                    </div>

                    {/* Slot Type with Color Coding and Icon */}
                    {(() => {
                      const config = getSlotTypeConfig(slot.slot_type);
                      const IconComponent = config.icon;
                      return (
                        <div
                          className={`px-3 py-2 rounded-full text-xs font-medium inline-flex items-center gap-2 border-2 ${config.color} ${config.textColor}`}
                        >
                          <IconComponent size={14} className={config.iconColor} />
                          <span>
                            {slot.slot_type || "Unknown"}
                          </span>
                        </div>
                      );
                    })()}

                    <div
                      className={`px-3 py-1 rounded-full text-xs font-medium inline-block ml-2 ${
                        slot.is_booked
                          ? "bg-red-200 text-red-800"
                          : "bg-green-200 text-green-800"
                      }`}
                    >
                      {slot.is_booked ? "Booked" : "Available"}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSlotsPage;
